import {
  UsersRoundIcon,
  LightbulbIcon,
  HandHeartIcon,
  MegaphoneIcon,
  HandshakeIcon,
  LandmarkIcon,
  NetworkIcon,
  GraduationCapIcon,
  UsersIcon,
  BriefcaseIcon,
  SparklesIcon,
  CompassIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface Pillar {
  icon: LucideIcon
  title: string
  titleEs: string
  text: string
  textEs: string
}

export const PILLARS: Pillar[] = [
  {
    icon: UsersRoundIcon,
    title: 'National Connections',
    titleEs: 'Conexiones Nacionales',
    text: 'Connect organizations, leaders, professionals, and trusted partners across the United States and Honduras to strengthen relationships, share opportunities, and encourage collaboration.',
    textEs:
      'Conectar organizaciones, líderes, profesionales y socios de confianza en los Estados Unidos y Honduras para fortalecer relaciones, compartir oportunidades y fomentar la colaboración.',
  },
  {
    icon: LightbulbIcon,
    title: 'Organizational Capacity',
    titleEs: 'Capacidad Organizacional',
    text: 'Strengthen organizations through shared resources, peer learning, and access to expertise in governance, fundraising, planning, communications, and leadership development.',
    textEs:
      'Fortalecer las organizaciones mediante recursos compartidos, aprendizaje entre pares y acceso a experiencia en gobernanza, recaudación de fondos, planificación, comunicaciones y desarrollo de liderazgo.',
  },
  {
    icon: HandHeartIcon,
    title: 'Community Engagement',
    titleEs: 'Participación Comunitaria',
    text: 'Create opportunities to better connect with the communities organizations serve, whether in the United States or Honduras, while sharing local priorities, programs, events, and opportunities across the Network.',
    textEs:
      'Crear oportunidades para conectar mejor con las comunidades que sirven las organizaciones, ya sea en los Estados Unidos u Honduras, compartiendo prioridades locales, programas, eventos y oportunidades en toda la Red.',
  },
  {
    icon: MegaphoneIcon,
    title: 'National Visibility',
    titleEs: 'Visibilidad Nacional',
    text: 'Elevate the work, leadership, and impact of participating organizations, including U.S.-based organizations supporting programs and communities in Honduras, through storytelling, media outreach, events, and digital platforms.',
    textEs:
      'Elevar el trabajo, el liderazgo y el impacto de las organizaciones participantes, incluidas las organizaciones con sede en EE. UU. que apoyan programas y comunidades en Honduras, mediante narrativa, medios de comunicación, eventos y plataformas digitales.',
  },
  {
    icon: HandshakeIcon,
    title: 'Strategic Partnerships',
    titleEs: 'Alianzas Estratégicas',
    text: 'Build relationships with foundations, companies, institutions, and other partners to explore shared funding opportunities, joint initiatives, and collaborations that can strengthen work in both the United States and Honduras.',
    textEs:
      'Construir relaciones con fundaciones, empresas, instituciones y otros socios para explorar oportunidades de financiamiento compartido, iniciativas conjuntas y colaboraciones que fortalezcan el trabajo en los Estados Unidos y Honduras.',
  },
  {
    icon: LandmarkIcon,
    title: 'Civic Engagement',
    titleEs: 'Participación Cívica',
    text: 'Advance nonpartisan civic education, dialogue, research, and engagement with institutions and policymakers on issues affecting Honduran-American communities and the broader relationship between the United States and Honduras.',
    textEs:
      'Impulsar la educación cívica no partidista, el diálogo, la investigación y la interacción con instituciones y responsables de políticas sobre temas que afectan a las comunidades hondureño-americanas y la relación entre los Estados Unidos y Honduras.',
  },
]

export interface Participation {
  icon: LucideIcon
  title: string
  titleEs: string
  text: string
  textEs: string
}

export const PARTICIPATION: Participation[] = [
  {
    icon: NetworkIcon,
    title: 'Connect',
    titleEs: 'Conectar',
    text: 'Participate in national and regional conversations, peer exchanges, introductions, and networking opportunities with organizations and leaders across the United States and Honduras.',
    textEs:
      'Participar en conversaciones nacionales y regionales, intercambios entre pares, presentaciones y oportunidades de networking con organizaciones y líderes en los Estados Unidos y Honduras.',
  },
  {
    icon: GraduationCapIcon,
    title: 'Learn & Strengthen',
    titleEs: 'Aprender y Fortalecer',
    text: 'Access shared resources, trainings, peer learning, and connections to professionals and advisors who can help strengthen organizational capacity.',
    textEs:
      'Acceder a recursos compartidos, capacitaciones, aprendizaje entre pares y conexiones con profesionales y asesores que ayuden a fortalecer la capacidad organizacional.',
  },
  {
    icon: UsersIcon,
    title: 'Collaborate',
    titleEs: 'Colaborar',
    text: 'Explore opportunities to work with other organizations on shared initiatives, programs, community priorities, research, and events.',
    textEs:
      'Explorar oportunidades para trabajar con otras organizaciones en iniciativas compartidas, programas, prioridades comunitarias, investigación y eventos.',
  },
  {
    icon: BriefcaseIcon,
    title: 'Build Partnerships',
    titleEs: 'Construir Alianzas',
    text: 'Explore connections with foundations, companies, institutions, and other partners, including potential collaborative funding and partnership opportunities.',
    textEs:
      'Explorar conexiones con fundaciones, empresas, instituciones y otros socios, incluyendo posibles oportunidades de financiamiento y alianzas colaborativas.',
  },
  {
    icon: SparklesIcon,
    title: 'Amplify Impact',
    titleEs: 'Amplificar el Impacto',
    text: 'Share organizational programs, accomplishments, events, and stories to help elevate the work already happening across our communities.',
    textEs:
      'Compartir programas, logros, eventos e historias de la organización para ayudar a elevar el trabajo que ya se realiza en nuestras comunidades.',
  },
  {
    icon: CompassIcon,
    title: 'Help Shape the Network',
    titleEs: 'Ayudar a Formar la Red',
    text: 'Share priorities, needs, ideas, and expertise to help guide future Network activities and areas of collaboration.',
    textEs:
      'Compartir prioridades, necesidades, ideas y experiencia para guiar las futuras actividades y áreas de colaboración de la Red.',
  },
]

export interface JoinStep {
  title: string
  titleEs: string
  lead: string
  leadEs: string
  points: string[]
  pointsEs: string[]
}

export const JOIN_STEPS: JoinStep[] = [
  {
    title: 'Connect',
    titleEs: 'Conectar',
    lead: 'The Network brings together select, mission-aligned organizations committed to strengthening Honduran-American communities and communities in Honduras. Participation is by invitation.',
    leadEs:
      'La Red reúne a organizaciones selectas y alineadas con la misión, comprometidas con fortalecer las comunidades hondureño-americanas y las comunidades en Honduras. La participación es por invitación.',
    points: [],
    pointsEs: [],
  },
  {
    title: 'Apply',
    titleEs: 'Aplicar',
    lead: 'To participate, an organization must:',
    leadEs: 'Para participar, una organización debe:',
    points: [
      'Serve Honduran-American communities and/or communities in Honduras, and maintain an organizational presence or operations in the United States.',
      'Maintain a strong reputation and record of integrity, including responsible organizational and financial practices.',
      'Complete and submit the online application.',
      'Provide additional information as needed to verify eligibility and good standing.',
      'If registered as a 501(c)(3), organizations may also be asked to provide supporting documentation, including formation documents and the most recent IRS Form 990, 990-EZ, or 990-N, as applicable.',
    ],
    pointsEs: [
      'Servir a comunidades hondureño-americanas y/o comunidades en Honduras, y mantener presencia u operaciones organizacionales en los Estados Unidos.',
      'Mantener una sólida reputación y trayectoria de integridad, incluidas prácticas organizacionales y financieras responsables.',
      'Completar y enviar la solicitud en línea.',
      'Proporcionar información adicional según sea necesario para verificar la elegibilidad y el buen estado.',
      'Si está registrada como 501(c)(3), también se le podrá solicitar documentación de respaldo, incluidos documentos de constitución y el Formulario 990, 990-EZ o 990-N del IRS más reciente, según corresponda.',
    ],
  },
  {
    title: 'Participate & Invest',
    titleEs: 'Participar e Invertir',
    lead: 'We believe a strong national network requires shared participation and collective investment, while recognizing that every organization has different capacities.',
    leadEs:
      'Creemos que una red nacional sólida requiere participación compartida e inversión colectiva, reconociendo que cada organización tiene capacidades diferentes.',
    points: [
      'Make a suggested annual contribution of $500 to help support the Network’s shared activities and infrastructure. Flexible, blended, in-kind, or other equitable participation options are available based on organizational capacity.',
      'Share organizational programs, events, needs, opportunities, and updates with the Network.',
      'Participate in convenings, trainings, working groups, and collaborative activities as relevant and able.',
    ],
    pointsEs: [
      'Realizar una contribución anual sugerida de $500 para apoyar las actividades e infraestructura compartidas de la Red. Existen opciones flexibles, combinadas, en especie u otras formas equitativas de participación según la capacidad organizacional.',
      'Compartir programas, eventos, necesidades, oportunidades y actualizaciones de la organización con la Red.',
      'Participar en convocatorias, capacitaciones, grupos de trabajo y actividades colaborativas según sea pertinente y posible.',
    ],
  },
]

export const PRESIDENT_LETTER = {
  salutation: 'Dear Leaders,',
  salutationEs: 'Estimados Líderes,',
  paragraphs: [
    'Our Honduran-American community is at a defining moment. Across the country, organizations, leaders, businesses, and volunteers are already doing meaningful work in education, culture, community service, advocacy, humanitarian efforts, entrepreneurship, and many other areas. What we have not always had is a strong national space to connect those efforts and create greater opportunities to work together.',
    'That is why we are building the Honduran-American National Network. The Network is not intended to replace or duplicate the important work already being done. Its purpose is to strengthen that work by connecting organizations and leaders, sharing resources and expertise, creating opportunities for collaboration, increasing national visibility, and building stronger relationships with institutions and partners.',
    'The Honduran-American Association will serve as the convener and backbone of the Network, but we believe its strength must come from the organizations that participate. Members will have opportunities to share their priorities, identify needs, contribute their experience and relationships, and help shape the Network as it grows.',
    'Every organization brings something different to the table, and every organization has different needs and capacities. Our goal is to create a flexible national community where we can learn from one another, open doors for one another, and pursue opportunities that may be stronger when approached together.',
    'Most importantly, we want to build this with you, not simply for you. We would be honored to have your organization help shape this effort from the beginning and join us in building stronger organizations, greater connection, and greater impact for Honduran communities in the United States and Honduras.',
  ],
  paragraphsEs: [
    'Nuestra comunidad hondureño-americana se encuentra en un momento decisivo. En todo el país, organizaciones, líderes, empresas y voluntarios ya realizan un trabajo significativo en educación, cultura, servicio comunitario, defensa de derechos, esfuerzos humanitarios, emprendimiento y muchas otras áreas. Lo que no siempre hemos tenido es un espacio nacional sólido para conectar esos esfuerzos y crear mayores oportunidades de trabajar juntos.',
    'Por eso estamos construyendo la Red Nacional Hondureño-Americana. La Red no pretende reemplazar ni duplicar el importante trabajo que ya se realiza. Su propósito es fortalecer ese trabajo conectando organizaciones y líderes, compartiendo recursos y experiencia, creando oportunidades de colaboración, aumentando la visibilidad nacional y construyendo relaciones más sólidas con instituciones y socios.',
    'La Asociación Hondureño-Americana servirá como convocante y columna vertebral de la Red, pero creemos que su fortaleza debe provenir de las organizaciones que participan. Los miembros tendrán oportunidades de compartir sus prioridades, identificar necesidades, aportar su experiencia y relaciones, y ayudar a dar forma a la Red conforme crece.',
    'Cada organización aporta algo diferente, y cada organización tiene necesidades y capacidades distintas. Nuestra meta es crear una comunidad nacional flexible donde podamos aprender unos de otros, abrir puertas mutuamente y perseguir oportunidades que pueden ser más sólidas cuando se abordan en conjunto.',
    'Y lo más importante: queremos construir esto con ustedes, no simplemente para ustedes. Sería un honor que su organización ayude a dar forma a este esfuerzo desde el inicio y se una a nosotros para construir organizaciones más fuertes, mayor conexión y mayor impacto para las comunidades hondureñas en los Estados Unidos y en Honduras.',
  ],
  closing: 'With appreciation and optimism,',
  closingEs: 'Con aprecio y optimismo,',
  name: 'Jay Miranda',
  role: 'President & CEO, Honduran-American Association',
  roleEs: 'Presidente y CEO, Asociación Hondureño-Americana',
}
